import { useState, useEffect, useCallback, useRef } from "react";
import {
  saveColumnWidths,
  loadColumnWidths,
  constrainWidth,
} from "../utils/columnResize";

/**
 * Custom hook for managing resizable table columns in HTML tables
 * @param {string} tableId - Unique identifier for the table (for storage)
 * @param {Array} columns - Array of column definitions with keys and default widths
 * @returns {Object} Column widths and resize handlers
 */
export function useTableColumnResize(tableId = "default", columns = []) {
  // Extract default widths from column definitions
  const defaultWidths = columns.reduce((acc, col) => {
    acc[col.key] = col.width || "150px";
    return acc;
  }, {});

  // Initialize column widths
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = loadColumnWidths();
    const tableWidths = saved?.[tableId];
    if (!tableWidths) return defaultWidths;

    // Merge saved widths with defaults
    return Object.keys(defaultWidths).reduce((acc, key) => {
      acc[key] = tableWidths[key] || defaultWidths[key];
      return acc;
    }, {});
  });

  const resizeTimeoutRef = useRef(null);
  const tableRef = useRef(null);

  // Save column widths to localStorage (debounced)
  const saveWidths = useCallback(
    (widths) => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        const allSaved = loadColumnWidths() || {};
        allSaved[tableId] = widths;
        saveColumnWidths(allSaved);
      }, 500); // Debounce save for 500ms
    },
    [tableId],
  );

  // Handle column resize
  const handleColumnResize = useCallback(
    (columnKey, newWidth) => {
      const widthPx = `${newWidth}px`;

      setColumnWidths((prev) => {
        const updated = {
          ...prev,
          [columnKey]: widthPx,
        };

        // Save to localStorage
        saveWidths(updated);

        return updated;
      });
    },
    [saveWidths],
  );

  // Reset to default widths
  const resetColumnWidths = useCallback(() => {
    setColumnWidths(defaultWidths);

    // Remove saved widths for this table
    const allSaved = loadColumnWidths() || {};
    delete allSaved[tableId];
    saveColumnWidths(allSaved);
  }, [defaultWidths, tableId]);

  // Auto-size columns based on actual content
  const autoSizeColumns = useCallback(() => {
    if (!tableRef.current) return;

    const autoWidths = {};

    // Create a temporary canvas for text measurement
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font =
      '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

    // Minimum widths for readability - increased for better spacing
    const minWidths = {
      name: 180,
      email: 240,
      phoneNumber: 160,
      phone: 160,
      company: 180,
      clientName: 180,
      role: 140,
      status: 120,
      clientStatus: 140,
      servicesProvided: 240,
      leadSource: 160,
      address: 240,
      clientOf: 180,
      contactCount: 120,
      actions: 100,
      id: 100,
    };

    columns.forEach((column) => {
      const columnKey = column.key;
      let maxWidth = 0;

      // Measure header text with generous padding
      const headerText = column.label || columnKey;
      const headerWidth = context.measureText(headerText).width + 60; // 30px padding each side
      maxWidth = Math.max(maxWidth, headerWidth);

      // Measure content in visible cells
      const table = tableRef.current;
      const columnIndex = columns.findIndex((col) => col.key === columnKey);

      if (columnIndex !== -1) {
        // Get all cells in this column
        const cells = table.querySelectorAll(
          `td:nth-child(${columnIndex + 1})`,
        );

        cells.forEach((cell) => {
          // Get text content from the cell
          const text = cell.textContent || "";
          // Add extra padding for content to prevent truncation
          const textWidth = context.measureText(text).width + 70; // 35px padding each side plus buffer
          maxWidth = Math.max(maxWidth, textWidth);
        });
      }

      // Apply minimum width constraint
      const minWidth = minWidths[columnKey] || 120;
      const finalWidth = Math.max(minWidth, Math.ceil(maxWidth));

      // Increase maximum width cap to prevent truncation
      const cappedWidth = Math.min(finalWidth, 600);

      autoWidths[columnKey] = `${cappedWidth}px`;
    });

    setColumnWidths(autoWidths);
    saveWidths(autoWidths);
  }, [columns, saveWidths]);

  // Initialize resize functionality
  const initializeResize = useCallback(
    (table) => {
      if (!table) return;

      tableRef.current = table;
      let isResizing = false;
      let currentColumn = null;
      let startX = 0;
      let startWidth = 0;

      // Create resize handles
      const headers = table.querySelectorAll("th");
      headers.forEach((header, index) => {
        if (index === headers.length - 1) return; // Skip last column

        // Remove existing handle if any
        const existingHandle = header.querySelector(".column-resize-handle");
        if (existingHandle) {
          existingHandle.remove();
        }

        // Create new handle
        const handle = document.createElement("div");
        handle.className = "column-resize-handle";
        handle.innerHTML = '<div class="resize-line"></div>';
        handle.dataset.column = columns[index]?.key;
        header.appendChild(handle);

        // Apply saved width
        const columnKey = columns[index]?.key;
        if (columnKey && columnWidths[columnKey]) {
          header.style.width = columnWidths[columnKey];

          // Also apply to all cells in this column
          const cells = table.querySelectorAll(`td:nth-child(${index + 1})`);
          cells.forEach((cell) => {
            cell.style.width = columnWidths[columnKey];
          });
        }
      });

      // Mouse down handler
      const handleMouseDown = (e) => {
        if (!e.target.classList.contains("column-resize-handle")) return;

        isResizing = true;
        currentColumn = e.target.dataset.column;
        startX = e.pageX;

        const header = e.target.parentElement;
        startWidth = header.offsetWidth;

        // Add resizing class to disable hover effects
        table.classList.add("resizing");

        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        e.preventDefault();
      };

      // Mouse move handler
      const handleMouseMove = (e) => {
        if (!isResizing) return;

        const diff = e.pageX - startX;
        const newWidth = constrainWidth(startWidth + diff);

        // Find and update the column width (header and all cells)
        const columnIndex = columns.findIndex(
          (col) => col.key === currentColumn,
        );
        if (columnIndex !== -1) {
          // Update header
          const headers = table.querySelectorAll("th");
          if (headers[columnIndex]) {
            headers[columnIndex].style.width = `${newWidth}px`;
          }

          // Update all cells in this column
          const cells = table.querySelectorAll(
            `td:nth-child(${columnIndex + 1})`,
          );
          cells.forEach((cell) => {
            cell.style.width = `${newWidth}px`;
          });
        }
      };

      // Mouse up handler
      const handleMouseUp = (e) => {
        if (!isResizing) return;

        isResizing = false;

        const diff = e.pageX - startX;
        const finalWidth = constrainWidth(startWidth + diff);

        // Remove resizing class
        table.classList.remove("resizing");

        // Call resize callback
        if (currentColumn) {
          handleColumnResize(currentColumn, finalWidth);
        }

        document.body.style.cursor = "";
        document.body.style.userSelect = "";

        currentColumn = null;
      };

      // Attach event listeners
      table.addEventListener("mousedown", handleMouseDown);
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      // Cleanup function
      return () => {
        table.removeEventListener("mousedown", handleMouseDown);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    },
    [columns, columnWidths, handleColumnResize],
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  return {
    columnWidths,
    handleColumnResize,
    resetColumnWidths,
    autoSizeColumns,
    initializeResize,
    defaultWidths,
  };
}

export default useTableColumnResize;
